import React, { useState, useEffect } from 'react';
import api from '@src/../js/api';
import useMobileBreakpoint from '@src/hooks/useMobileBreakpoint';
interface Variant {
  id: string;
  title: string;
  subtext?: string;
  price?: number;
  available: boolean;
}
interface TagOption {
  id: string;
  title: string;
  subtext?: string;
  price?: null;
  available: boolean;
  isTag: boolean;
  productId?: string;
  productHandle?: string;
  additionalDetails?: string; // For tag-based options, additional details can be shown
}
interface Product {
  id: string;
  handle?: string;
  variants?: Variant[];
  tags?: string[];
}
interface TagProduct {
  id: string;
  title: string;
  handle: string;
  tags: string[];
  variants: {
    edges: Array<{
      node: {
        id: string;
        availableForSale: boolean;
        price: {
          amount: string;
        };
      };
    }>;
  };
}
interface ComponentProps {
  block_id?: string;
  block_settings?: any;
  product?: Product;
  product_form_id?: string;
  [key: string]: any;
}
export const StickySizeSelector = (props: ComponentProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [tagProducts, setTagProducts] = useState<TagProduct[]>([]);
  const [isLoadingTagProducts, setIsLoadingTagProducts] = useState(false);
  const isMobile = useMobileBreakpoint(props.settings.globals.responsive_breakpoints?.mobile_max || 767);
  // Extract settings from block_settings with defaults
  const {
    option,
    selector_style,
    selector_style_mobile,
    show_label = true,
    placeholder_text = 'Choose an option',
    unavailable_text = 'Sold out',
    show_price_subtext = true,
    variant_source = 'variants',
    tag_name = '',
    field_to_display = 'title',
    tag_prefix_to_display = '',
    // Action settings
    tag_selection_action = 'redirect', // "redirect" or "variant_change"
    // Styling settings
    max_width = 400,
    border_radius = 6
  } = props.settings.block_settings;
  props = props.settings;
  const product = props.product;
  const product_form_id = props.product_form_id;
  const block_id = props.block_id?.replace(/_/g, '').toLowerCase() || 'size-selector';
  const fetchProductsByTag = async (tagFilter: string): Promise<TagProduct[]> => {
    if (!tagFilter) return [];
    setIsLoadingTagProducts(true);
    const endpoint = '/api/2024-01/graphql.json';
    // Get the access token from window or use a default
    const accessToken =
      (window as any)?.Shopify?.theme?.storefront_access_token ||
      (window as any)?.STOREFRONT_ACCESS_TOKEN ||
      api.getStorefrontToken() ||
      null;
    if (!accessToken) {
      console.warn('No Shopify Storefront access token found. Falling back to regular variants.');
      setIsLoadingTagProducts(false);
      // Return empty array to trigger fallback to regular variants
      return [];
    }
    // Create search query - handle both exact tag matches and prefix matches
    // For exact matches, use the full tag value, for prefix searches use wildcard
    const isExactTag = tagFilter.includes(':');
    if (!isExactTag) {
      setIsLoadingTagProducts(false);
      return [];
    }
    const searchQuery = `tag:"${tagFilter}"`;
    const query = `
      query GetProductsByTag(
        $query: String!
        $first: Int!
      ) {
        search(
          query: $query
          first: $first
          types: [PRODUCT]
        ) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                tags
                availableForSale
                variants(first: 5) {
                  edges {
                    node {
                      id
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    const variables = {
      query: searchQuery,
      first: 10
    };
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({
          query: query,
          variables: variables
        })
      });
      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }
      const result = await response.json();
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        setIsLoadingTagProducts(false);
        return [];
      }
      const products = result?.data?.search?.edges?.map((edge: any) => edge.node) || [];
      // Filter products that actually contain the tag we're looking for
      const filteredProducts = products.filter((product: TagProduct) => {
        if (isExactTag) {
          // For exact tag matches, look for the exact tag value
          return product.tags.some((tag) => tag.toLowerCase() === tagFilter.toLowerCase());
        } else {
          // For prefix matches, look for tags that start with the prefix
          return product.tags.some((tag) => tag.toLowerCase().includes(tagFilter.toLowerCase()));
        }
      });
      setIsLoadingTagProducts(false);
      return filteredProducts;
    } catch (error) {
      console.error('Error fetching products by tag:', error);
      setIsLoadingTagProducts(false);
      return [];
    }
  };
  // Load tag products when variant_source is "tags" and tag_name is provided
  useEffect(() => {
    if (variant_source === 'tags' && tag_name && product?.tags) {
      // Find the actual tag value from the current product's tags
      const currentProductTag = product.tags.find((tag) => tag.toLowerCase().startsWith(tag_name.toLowerCase()));
      if (currentProductTag) {
        fetchProductsByTag(currentProductTag).then((products) => {
          setTagProducts(products);
        });
      } else {
        // Fallback to the original tag_name if no matching tag found
        fetchProductsByTag(tag_name).then((products) => {
          setTagProducts(products);
        });
      }
    }
  }, [variant_source, tag_name, product?.tags]);
  // Generate options based on variant_source
  const getOptions = (): (Variant | TagOption)[] => {
    if (variant_source === 'tags') {
      if (isLoadingTagProducts) {
        return [
          {
            id: 'loading',
            title: 'Loading options...',
            price: null,
            available: false,
            isTag: true
          }
        ];
      }
      if (tagProducts.length === 0) {
        // If no tag products found, fall back to regular variants if available
        if (product?.variants && product.variants.length > 1) {
          return product.variants;
        }
        return [];
      }
      // Convert tag products to options
      const tagOptions: TagOption[] = tagProducts.map((tagProduct) => {
        // Extract the variant info for pricing/availability
        const firstVariant = tagProduct.variants?.edges?.[0]?.node;
        const isAvailable = tagProduct.availableForSale && (firstVariant?.availableForSale || false);
        // Create a clean title by finding and processing the matching tag
        let cleanTitle = tagProduct.title;
        let addtionalDetails = '';
        if (field_to_display === 'first_variant_title') {
          cleanTitle = firstVariant?.title || '';
        } else if (field_to_display === 'tag') {
          const tags = tagProduct.tags || [];
          cleanTitle =
            tags
              .find((tag) => tag.toLowerCase().startsWith(`${tag_prefix_to_display.toLowerCase()}:`))
              ?.split(':')?.[1] || '';
          if (cleanTitle.includes('_')) {
            const parts = cleanTitle.split('_');
            cleanTitle = parts[0].trim();
            addtionalDetails = parts[1]?.trim();
          }
        }
        if (!cleanTitle) return null;
        return {
          id: tagProduct.id,
          title: `${cleanTitle}`,
          subtext: ``, // Show full product title as subtext
          price: null, // We don't show price for tag-based options in the dropdown
          available: isAvailable,
          isTag: true,
          productId: tagProduct.id,
          productHandle: tagProduct.handle,
          additionalDetails: addtionalDetails || ''
        };
      });
      // Sort by availability (available first) then by title
      return tagOptions
        .filter((option) => option)
        .sort((a, b) => {
          const extractNumber = (val: string | undefined | null): number | null => {
            const match = val?.trim().match(/^(\d+(?:\.\d+)?)/); // extract leading number
            return match ? parseFloat(match[1]) : null;
          };
          const aVal = a.additionalDetails ?? '';
          const bVal = b.additionalDetails ?? '';
          const aNum = extractNumber(aVal);
          const bNum = extractNumber(bVal);
          if (aNum !== null && bNum !== null) {
            return aNum - bNum;
          } else if (aNum !== null) {
            return -1;
          } else if (bNum !== null) {
            return 1;
          } else {
            return aVal.localeCompare(bVal);
          }
        });
    }
    // Default to variants
    return product?.variants || [];
  };
  // Calculate options (will be recalculated when tagProducts changes)
  const sizeOptions = getOptions();
  // Set initial selected option - this effect runs when options change
  useEffect(() => {
    // Don't set selection while still loading
    if (isLoadingTagProducts) {
      return;
    }
    const parent = document.querySelector(
      `[data-sticky-size-selector="sticky-size-selector-block--${props.block_id}"]`
    );
    if (sizeOptions.length > 0 && sizeOptions.some((option) => option.id.includes(props.product?.id || ''))) {
      if (parent) {
        parent.classList.remove('hidden');
      }
      if (variant_source === 'tags' && product?.handle) {
        // For tag-based variants, find the option that matches the current product handle
        const currentProductOption = sizeOptions.find(
          (option) => 'productHandle' in option && option.productHandle === product.handle
        );
        if (currentProductOption && (!selectedOption || selectedOption.id !== currentProductOption.id)) {
          setSelectedOption(currentProductOption);
          return;
        }
      }
      // Fallback: select first available option if no current product match or for regular variants
      if (!selectedOption) {
        const firstAvailable = sizeOptions.find((option) => option.available);
        const selected = firstAvailable || sizeOptions[0];
        setSelectedOption(selected);
      }
    } else {
      if (parent) {
        parent.classList.add('hidden');
      }
    }
  }, [sizeOptions, variant_source, product?.handle, isLoadingTagProducts, selectedOption]);
  const formatPrice = (price?: number | null): string => {
    if (!price) return '';
    // Simple price formatting - in a real implementation, use Shopify's money filter
    return `$${(price / 100).toFixed(2)}`;
  };
  const styles = {
    container: {
      position: 'relative' as const,
      width: '100%',
      maxWidth: max_width > 0 ? `${max_width}px` : 'auto'
    },
    button: {
      width: '100%',
      padding: `8px 12px`,
      backgroundColor: '#FFFFFF',
      borderRadius: `${border_radius}px`,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between' as const,
      alignItems: 'center',
      fontSize: `18px`,
      color: '#000000',
      transition: 'all 0.2s ease',
      outline: 'none',
      textAlign: 'left' as const,
      minHeight: '44px'
    },
    buttonText: {
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    dropdownIcon: {
      width: '16px',
      height: '16px',
      marginLeft: '8px',
      flexShrink: 0,
      transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }
  };
  const handleButtonClick = () => {
    const newActiveState = !isActive;
    setIsActive(newActiveState);
    // Determine panel title based on mode
    const getPanelTitle = () => {
      if (isMobile) {
        // Mobile: individual panel, use custom title or default
        return option || 'Size';
      } else {
        // Desktop: combined panel, don't set panelTitle so it uses defaults in render functions
        return undefined;
      }
    };
    // Dispatch event to show/hide selection panel
    window.dispatchEvent(
      new CustomEvent('stickybar:togglePanel', {
        detail: {
          type: isMobile ? 'size' : 'combined',
          isActive: newActiveState,
          blockId: block_id,
          panelTitle: getPanelTitle(),
          sizeOptions: sizeOptions, // Always use the processed options array
          colourOptions: isMobile ? [] : (window as any).stickyColorOptions || [], // Get color options from global if available
          selectedSizeOption: selectedOption,
          selectedColourOption: isMobile ? null : (window as any).stickySelectedColor || null,
          onSizeSelect: (window as any).stickySizeSelectHandler || null,
          onColorSelect: isMobile ? null : (window as any).stickyColorSelectHandler || null,
          productId: product?.id || '',
        }
      })
    );
  };
  // Listen for panel close events and option selections
  useEffect(() => {
    const handlePanelClose = () => {
      setIsActive(false);
    };
    const handlePanelToggle = (event: CustomEvent) => {
      const data = event.detail;
      if (!isMobile && data.type === 'combined') {
        // On desktop, both selectors should show active state when combined panel is open
        setIsActive(data.isActive);
      } else if (isMobile) {
        // On mobile, if another selector opens, close this one
        if (data.type === 'colour' && data.blockId !== block_id && data.isActive) {
          setIsActive(false);
        }
      }
    };
    const handleOptionSelected = (event: CustomEvent) => {
      const { type, selectedOption: newSelection, blockId } = event.detail;
      // Only handle if this event is for this specific block and type
      if (type === 'size' && blockId === block_id) {
        setSelectedOption(newSelection);
        setIsActive(false);
        // Handle form updates if needed
        if (product_form_id) {
          const variantInput = document.querySelector(`#${product_form_id} input[name="id"]`) as HTMLInputElement;
          if (variantInput && newSelection?.id) {
            variantInput.value = newSelection.id;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    };
    window.addEventListener('stickybar:panelClosed', handlePanelClose);
    window.addEventListener('stickybar:togglePanel', handlePanelToggle as EventListener);
    window.addEventListener('stickybar:optionSelected', handleOptionSelected as EventListener);
    return () => {
      window.removeEventListener('stickybar:panelClosed', handlePanelClose);
      window.removeEventListener('stickybar:togglePanel', handlePanelToggle as EventListener);
      window.removeEventListener('stickybar:optionSelected', handleOptionSelected as EventListener);
    };
  }, [block_id, product_form_id, isMobile]);
  // Store size options and handlers globally for cross-component access
  useEffect(() => {
    (window as any).stickySizeOptions = sizeOptions;
    (window as any).stickySelectedSize = selectedOption;
    (window as any).stickySizeSelectHandler = (option: any) => {
      setSelectedOption(option);
      // Handle form updates if needed
      if (product_form_id) {
        const variantInput = document.querySelector(`#${product_form_id} input[name="id"]`) as HTMLInputElement;
        if (variantInput && option?.id) {
          variantInput.value = option.id;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };
  }, [sizeOptions, selectedOption, product_form_id]);
  // if ( variant_source === "variants" && options.length === 0 || variant_source === "tags" && tagProducts.length === 0) {
  //   return null; // Don't render if no size options available
  // }

  if (sizeOptions.length === 0 || !sizeOptions.some((option) => option.id.includes(props.product?.id || '')))
    return null;

  return (
    <div
      className={`sticky-size-selector-${block_id}`}
      style={styles.container}
      data-block-id={block_id}
    >
      <button
        type='button'
        style={styles.button}
        onClick={handleButtonClick}
        aria-expanded={isActive}
        aria-haspopup='true'
      >
        <div
          className={`${isMobile ? `${selector_style_mobile}` : `${selector_style}`} space-x-2`}
          style={styles.buttonText}
        >
          <span>{selectedOption ? selectedOption.title : placeholder_text}</span>
          {selectedOption && selectedOption.additionalDetails && <span>{selectedOption.additionalDetails}</span>}
        </div>
        <svg
          style={styles.dropdownIcon}
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
        >
          <polyline points='6,9 12,15 18,9'></polyline>
        </svg>
      </button>
    </div>
  );
};
